import React from "react";
import { ChevronDown, TriangleAlert } from "lucide-react";
import {
  CalendarTransaction,
  FunderGroup,
  FunderReturn,
  RESOLVABLE_CADENCES,
  ResolvableCadence,
} from "@/types/underwriting";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { cn } from "@/lib/utils";
import { withdrawalInLeverage } from "./funderLeverage";
import { isSameFunder } from "./funderName";
import { withdrawalKey } from "./withdrawalOverrides";

interface FunderCardProps {
  funder: FunderGroup;
  isSelected: boolean;
  onNameClick: (funderName: string) => void;
  onHighlightDates: (dates: string[]) => void;
  onToggleActive: (funderName: string, withdrawalIndex: number) => void;
  onWithdrawalClick?: (funderName: string, amount: number) => void;
  onDepositClick?: (funderName: string, amount: number, date: string) => void;
  calendarTransactions: CalendarTransaction[];
  /** Keyed by withdrawalKey, not array index — see funderLeverage. */
  withdrawalActiveOverrides: Record<string, boolean>;
  /**
   * Cadences assigned in the UI (optimistic / local). Keyed the same way as
   * the active overrides.
   */
  cadenceOverrides?: Record<string, ResolvableCadence>;
  /**
   * Persist a frequency change. When provided, every row shows a frequency
   * dropdown (Daily / Weekly / Bi-weekly / Monthly).
   */
  onChangeFrequency?: (withdrawalIndex: number, cadence: ResolvableCadence) => void;
  /**
   * For viewers with no business toggling a position at all (the broker view).
   */
  activeToggleDisabled?: boolean;
  /** When true, frequency is display-only (no dropdown). */
  frequencyDisabled?: boolean;
}

/** Display labels for the frequency dropdown, in required order. */
const CADENCE_LABELS: Record<ResolvableCadence, string> = {
  daily: "Daily",
  weekly: "Weekly",
  biweekly: "Bi-weekly",
  monthly: "Monthly",
  unknown: "Unknown",
};

const formatCurrency = (amount: number): string => {
  const whole = Math.abs(amount - Math.round(amount)) < 0.005;
  if (!whole) {
    return amount.toLocaleString("en-US", { style: "currency", currency: "USD" });
  }
  const rounded = Math.round(amount);
  return Math.abs(rounded) >= 1000 ? `$${rounded.toLocaleString()}` : `$${rounded}`;
};

const formatDate = (iso: string): string => {
  try {
    const d = new Date(iso + "T00:00:00");
    const month = d.toLocaleDateString("en-US", { month: "short" });
    const day = d.getDate();
    const year = d.getFullYear().toString().slice(-2);
    return `${month} ${day}, ${year}`;
  } catch {
    return iso;
  }
};

const clickableClass = "cursor-pointer select-none hover:underline";

const asResolvableCadence = (value: string | undefined): ResolvableCadence | null => {
  if (!value) return null;
  return (RESOLVABLE_CADENCES as readonly string[]).includes(value)
    ? (value as ResolvableCadence)
    : null;
};

const headerCellClass = "text-[0.6rem] font-semibold uppercase tracking-wide text-muted-foreground";

const FunderCard: React.FC<FunderCardProps> = ({
  funder,
  isSelected,
  onNameClick,
  onHighlightDates,
  onToggleActive,
  onWithdrawalClick,
  onDepositClick,
  calendarTransactions,
  withdrawalActiveOverrides,
  cadenceOverrides,
  onChangeFrequency,
  activeToggleDisabled = false,
  frequencyDisabled = false,
}) => {
  const funderTxns = calendarTransactions.filter((tx) => isSameFunder(tx.funder, funder.name));
  const isLocOrLoan = funder.position_type === "loc" || funder.position_type === "loan";
  const withdrawalLabel = isLocOrLoan ? "Payments" : "Withdrawals";
  const depositLabel = isLocOrLoan ? "Draws" : "Deposits";
  const canEditFrequency = !!onChangeFrequency && !frequencyDisabled;
  // Backend _detect_returns attaches these to each schedule; flatten for the card.
  const returns: FunderReturn[] = funder.withdrawals
    .flatMap((w) => w.returns ?? [])
    .slice()
    .sort((a, b) => a.date.localeCompare(b.date));

  const handleFunderNameClick = () => {
    const dates = funderTxns.map((tx) => tx.date);
    onHighlightDates(dates);
    onNameClick(funder.name);
  };

  const handleDepositClick = (dep: { date: string; amount: number }) => {
    onHighlightDates([dep.date]);
    onDepositClick?.(funder.name, dep.amount, dep.date);
  };

  const handleReturnClick = (ret: FunderReturn) => {
    onHighlightDates([ret.date]);
    // Returns are credits in the txn list; reuse the deposit amount+date filter.
    onDepositClick?.(funder.name, ret.amount, ret.date);
  };

  const handleWithdrawalClick = (withdrawalAmount: number) => {
    const dates = funderTxns
      .filter((tx) => tx.tx_type === "withdrawal" && Math.abs(tx.amount - withdrawalAmount) < 0.01)
      .map((tx) => tx.date);
    onHighlightDates(dates);
    onWithdrawalClick?.(funder.name, withdrawalAmount);
  };

  const hasActivity =
    funder.deposits.length > 0 || funder.withdrawals.length > 0 || returns.length > 0;

  return (
    <div
      className={cn(
        "border-l-[3px] border-t border-l-muted border-t-border bg-card p-3 transition-colors duration-150",
        isSelected && "bg-muted/50"
      )}
    >
      <button
        type="button"
        className={cn("mb-2 block text-left text-base font-bold", clickableClass)}
        onClick={handleFunderNameClick}
      >
        {funder.name}
      </button>

      {!hasActivity ? (
        <p className="text-[0.75rem] text-muted-foreground">No activity</p>
      ) : (
        <>
          {funder.withdrawals.length > 0 && (
            <div className="mb-2">
              <span className="mb-1.5 block text-[0.6rem] font-semibold uppercase tracking-wide text-muted-foreground">
                {withdrawalLabel}
              </span>
              <div
                className="grid min-w-0 items-center gap-x-2 gap-y-1"
                style={{
                  // Frequency sized for longest label ("Bi-weekly"); occurrence
                  // sits immediately after; 1fr parks the active toggle at the
                  // trailing edge so it doesn't clip when the panel is narrow.
                  gridTemplateColumns: "auto 6rem auto 1fr",
                }}
              >
                <span className={headerCellClass}>Amount</span>
                <span className={headerCellClass}>Frequency</span>
                <span className={headerCellClass}>Occurrence</span>
                <span className="w-2 justify-self-end" aria-hidden />

                {funder.withdrawals.map((w, i) => {
                  const wKey = withdrawalKey(w, i);
                  const resolvedCadence = cadenceOverrides?.[wKey];
                  const displayCadence =
                    resolvedCadence ?? asResolvableCadence(w.frequency) ?? null;
                  const isActive = withdrawalInLeverage(w, withdrawalActiveOverrides[wKey]);
                  const isUnknown =
                    (resolvedCadence ?? w.frequency) === "unknown" || !displayCadence;

                  return (
                    <React.Fragment key={i}>
                      <button
                        type="button"
                        className={cn(
                          "justify-self-start rounded-md border px-2 py-1 text-left text-[0.75rem] font-semibold",
                          isActive
                            ? "border-primary/30 bg-primary/5 text-primary"
                            : "border-border/50 bg-muted/20 text-muted-foreground",
                          !isActive && "opacity-70"
                        )}
                        onClick={() => handleWithdrawalClick(w.amount)}
                      >
                        {formatCurrency(w.amount)}
                      </button>

                      {canEditFrequency ? (
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <button
                              type="button"
                              className={cn(
                                "flex h-7 w-full min-w-0 items-center justify-between gap-0.5 rounded-md border px-1.5 text-[0.7rem]",
                                isUnknown
                                  ? "border-amber-500/50 bg-amber-500/10 text-amber-700 dark:text-amber-500"
                                  : isActive
                                    ? "border-primary/20 bg-primary/5 text-foreground"
                                    : "border-border/40 bg-muted/20 text-muted-foreground",
                                !isActive && !isUnknown && "opacity-70"
                              )}
                              title={
                                isUnknown
                                  ? "No schedule detected. Pick one to include this position in leverage."
                                  : "Change payment frequency"
                              }
                            >
                              <span className="flex min-w-0 items-center gap-0.5 truncate">
                                {isUnknown && <TriangleAlert className="h-3 w-3 shrink-0" />}
                                <span className="truncate">
                                  {displayCadence ? CADENCE_LABELS[displayCadence] : "Unknown"}
                                </span>
                              </span>
                              <ChevronDown className="h-3 w-3 shrink-0 opacity-60" />
                            </button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="start" className="w-36">
                            {RESOLVABLE_CADENCES.map((cadence) => (
                              <DropdownMenuItem
                                key={cadence}
                                className="text-xs"
                                onSelect={() => onChangeFrequency?.(i, cadence)}
                              >
                                {CADENCE_LABELS[cadence]}
                              </DropdownMenuItem>
                            ))}
                          </DropdownMenuContent>
                        </DropdownMenu>
                      ) : (
                        <span
                          className={cn(
                            "justify-self-start truncate rounded-md border px-1.5 py-1 text-[0.65rem]",
                            isUnknown
                              ? "border-amber-500/50 bg-amber-500/10 text-amber-700 dark:text-amber-500"
                              : "border-border/40 bg-muted/20 text-muted-foreground",
                            !isActive && "opacity-70"
                          )}
                        >
                          {displayCadence
                            ? CADENCE_LABELS[displayCadence]
                            : w.frequency || "Unknown"}
                        </span>
                      )}

                      <button
                        type="button"
                        className={cn(
                          "justify-self-start rounded-md border px-2 py-1 text-[0.65rem] text-muted-foreground",
                          isActive
                            ? "border-primary/20 bg-primary/5"
                            : "border-border/40 bg-muted/20 opacity-70"
                        )}
                        onClick={() => handleWithdrawalClick(w.amount)}
                      >
                        x{w.count}
                      </button>

                      <button
                        type="button"
                        disabled={activeToggleDisabled}
                        className={cn(
                          "h-2 w-2 shrink-0 justify-self-end rounded-full",
                          isActive ? "bg-primary" : "bg-muted-foreground",
                          activeToggleDisabled ? "cursor-default" : "hover:opacity-85"
                        )}
                        aria-label={isActive ? "Active" : "Inactive"}
                        onClick={(e) => {
                          e.stopPropagation();
                          if (activeToggleDisabled) return;
                          onToggleActive(funder.name, i);
                        }}
                      />
                    </React.Fragment>
                  );
                })}
              </div>
            </div>
          )}

          {returns.length > 0 && (
            <div className={cn(funder.deposits.length > 0 ? "mb-2" : undefined)}>
              <span className="mb-0.5 block text-[0.6rem] font-semibold uppercase tracking-wide text-muted-foreground">
                Returns
              </span>
              {returns.map((ret, i) => (
                <button
                  key={`${ret.date}-${ret.amount}-${ret.index ?? i}`}
                  type="button"
                  className={cn("mb-0.5 flex w-full items-center justify-between", clickableClass)}
                  onClick={() => handleReturnClick(ret)}
                >
                  <span className="text-[0.75rem] font-normal text-muted-foreground">
                    Return {formatCurrency(ret.amount)}
                  </span>
                  <span className="text-[0.65rem] text-muted-foreground">
                    {formatDate(ret.date)}
                  </span>
                </button>
              ))}
            </div>
          )}

          {funder.deposits.length > 0 && (
            <div>
              <span className="mb-0.5 block text-[0.6rem] font-semibold uppercase tracking-wide text-muted-foreground">
                {depositLabel}
              </span>
              {funder.deposits.map((dep, i) => (
                <button
                  key={i}
                  type="button"
                  className={cn("mb-0.5 flex w-full items-center justify-between", clickableClass)}
                  onClick={() => handleDepositClick(dep)}
                >
                  <span className="text-[0.75rem] font-semibold text-muted-foreground">
                    {formatCurrency(dep.amount)}
                  </span>
                  <span className="text-[0.65rem] text-muted-foreground">
                    {formatDate(dep.date)}
                  </span>
                </button>
              ))}
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default FunderCard;
